import {
  Command,
  dayMonthDateString,
  Shift,
  shiftTimeRangeDurationString,
  Store,
  User,
} from '@nextforce/api'
import { Divider, Input, makeStyles, Text } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import { Image } from 'expo-image'
import React, { useCallback, useState } from 'react'
import { View } from 'react-native'
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view'
import { CommandButtons } from 'shared/components/command_buttons'
import { PressableOpacity } from 'shared/components/pressable_opacity'
import { UserPositionAndAttribute } from 'shared/components/shift/user_position_attributes'
interface Props {
  shift: Shift
  onStorePress?: (store: Store) => void
  onButtonPress(command: Command, note?: string): Promise<void>
}

export function HelperShiftDetails({
  shift,
  onStorePress,
  onButtonPress,
}: Props) {
  const [note, setNote] = useState<string | undefined>()

  const onPress = useCallback(
    async (command: Command) => {
      await onButtonPress(command, note)
    },
    [note, onButtonPress]
  )

  const styles = useStyles()
  return (
    <KeyboardAwareScrollView
      keyboardShouldPersistTaps={'handled'}
      enableOnAndroid={true}
    >
      <View style={styles.container}>
        <View>
          <View style={styles.card}>
            <UserPositionAndAttribute shift={shift} />
            <Divider style={styles.divider} />
            <TimingDetails shift={shift} />
            <Divider style={styles.divider} />
            {shift.helperProxyAssignmentMessage ? (
              <>
                <Text style={styles.proxyText}>
                  {shift.helperProxyAssignmentMessage}
                </Text>
                <Divider style={styles.divider} />
              </>
            ) : null}
            <StoreDetails
              store={shift.store}
              approver={shift.approver}
              onStorePress={onStorePress}
            />
          </View>
          <CandidateNote note={note} setNote={setNote} />
        </View>
        <View style={styles.buttonContainer}>
          <CommandButtons commands={shift.commands} onButtonPress={onPress} />
        </View>
      </View>
    </KeyboardAwareScrollView>
  )
}

interface TimingDetailsProps {
  shift: Shift
}
export function TimingDetails(props: TimingDetailsProps) {
  const styles = useStyles()

  return (
    <View style={{ margin: 10, paddingLeft: 5 }}>
      <Text style={styles.date}>{dayMonthDateString(props.shift.date)}</Text>
      <Text style={styles.timeRange}>
        {shiftTimeRangeDurationString(props.shift)}
      </Text>
    </View>
  )
}

interface StoreDetailsProps {
  store: Store
  onStorePress?: (store: Store) => void
  approver?: User
}

function StoreDetails(props: StoreDetailsProps) {
  const styles = useStyles()

  return (
    <PressableOpacity
      style={styles.positionRow}
      onPress={() => {
        if (props.onStorePress) {
          props.onStorePress(props.store)
        }
      }}
    >
      <Image
        source={require('assets/open_shifts/map-icon.png')}
        style={styles.mapIcon}
      />
      <View>
        <Text style={styles.storeDetailstext}>
          {`${props.store?.name} (${props.store?.storeNumber})`}
        </Text>
        <Text style={styles.storeDetailstext}>{props.store?.owner}</Text>
        <Text style={styles.shiftApprovertext}>
          {props.approver?.firstName &&
            `Shift Manager: ${props.approver?.firstName} ${props.approver?.lastName}`}
        </Text>
      </View>
    </PressableOpacity>
  )
}

interface CandidateNoteProps {
  note?: string
  setNote: React.Dispatch<React.SetStateAction<string | undefined>>
}

export function CandidateNote({ note, setNote }: CandidateNoteProps) {
  const styles = useStyles()
  return (
    <View style={styles.inputContainer}>
      <Input
        style={styles.input}
        placeholder={t('requestNote')}
        value={note}
        onChangeText={setNote}
        multiline
      />
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  container: {
    flex: 1,
    backgroundColor: theme.colors?.grey5,
  },
  card: {
    justifyContent: 'center',
    backgroundColor: theme.colors?.white,
    shadowColor: theme.colors?.black,
    shadowOffset: {
      width: 0,
      height: 3,
    },
    shadowOpacity: 0.29,
    shadowRadius: 4.65,
  },
  positionRow: {
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center',
  },
  date: {
    fontSize: 19,
    fontFamily: font.bold,
  },
  timeRange: {
    color: theme.colors?.grey0,
    marginTop: 5,
    fontSize: 16,
  },
  mapIcon: {
    height: 35,
    width: 30,
    margin: 20,
  },
  storeDetailstext: {
    fontSize: 15,
    fontFamily: font.bold,
  },
  shiftApprovertext: {
    fontSize: 15,
    color: 'gray',
  },
  divider: {
    backgroundColor: theme.colors?.grey0,
    width: '100%',
  },
  buttonContainer: {
    margin: 10,
  },
  input: {
    height: 80,
    textAlignVertical: 'top',
    paddingVertical: 10,
    color: 'black',
    borderColor: theme.colors?.grey5,
  },
  inputContainer: {
    backgroundColor: 'white',
    margin: 10,
  },
  proxyText: {
    color: theme.colors.grey1,
    margin: 10,
    paddingLeft: 5,
    fontSize: 16,
  },
}))
